import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import AddressAutocomplete from '../components/AddressAutocomplete';

/**
 * Página de prueba para Google Maps
 * Ayuda a diagnosticar problemas con la configuración de Google Maps
 */
const GoogleMapsTest = () => {
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [apiKeyStatus, setApiKeyStatus] = useState('checking');
  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    if (!apiKey || apiKey === 'your_google_maps_api_key_here') {
      setApiKeyStatus('missing');
    } else {
      setApiKeyStatus('configured');
    }

    // Check if Google Maps script is loaded
    const checkScript = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setScriptLoaded(true);
        clearInterval(checkScript);
      }
    }, 500);

    // Clear interval after 10 seconds
    setTimeout(() => {
      clearInterval(checkScript);
    }, 10000);

    return () => clearInterval(checkScript);
  }, []);

  const handlePlaceSelected = (addressData) => {
    console.log('Place selected:', addressData);
    setSelectedPlace(addressData);
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Google Maps Diagnostic</h1>
          <p className="mt-1 text-sm text-gray-500">
            Use esta página para diagnosticar problemas con Google Maps
          </p>
        </div>

        {/* API Key Status */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Estado de Configuración</h2>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">API Key Status:</span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                apiKeyStatus === 'configured'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {apiKeyStatus === 'configured' ? '✓ Configured' : '✗ Not Configured'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Google Maps Script:</span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                scriptLoaded
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {scriptLoaded ? '✓ Loaded' : '⏳ Loading...'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">API Key Value:</span>
              <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?
                  `${process.env.REACT_APP_GOOGLE_MAPS_API_KEY.substring(0, 20)}...` :
                  'Not set'
                }
              </code>
            </div>
          </div>
        </div>

        {/* Test Area */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Test Address Autocomplete</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search for an address:
              </label>
              <AddressAutocomplete
                onPlaceSelected={handlePlaceSelected}
                initialValue=""
              />
            </div>

            {selectedPlace && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-sm font-medium text-green-800 mb-2">✓ Address Selected!</h3>
                <dl className="grid grid-cols-1 gap-2 text-sm">
                  <div>
                    <dt className="font-medium text-gray-700">Street Address:</dt>
                    <dd className="text-gray-900">{selectedPlace.streetAddress || '-'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-700">City:</dt>
                    <dd className="text-gray-900">{selectedPlace.city || '-'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-700">State:</dt>
                    <dd className="text-gray-900">{selectedPlace.state || '-'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-700">Postal Code:</dt>
                    <dd className="text-gray-900">{selectedPlace.postalCode || '-'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-700">Country:</dt>
                    <dd className="text-gray-900">{selectedPlace.country || '-'}</dd>
                  </div>
                  {selectedPlace.formattedAddress && (
                    <div>
                      <dt className="font-medium text-gray-700">Formatted Address:</dt>
                      <dd className="text-gray-900">{selectedPlace.formattedAddress}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-blue-900 mb-4">Instrucciones de Configuración</h2>

          <div className="space-y-4 text-sm text-blue-800">
            <div>
              <h3 className="font-medium mb-2">1. Verificar que el servidor React se reinició</h3>
              <p className="text-blue-700">
                Después de modificar el archivo .env, DEBES reiniciar el servidor de React:
              </p>
              <code className="block mt-2 bg-blue-100 px-3 py-2 rounded text-xs">
                # Presiona Ctrl+C para detener el servidor<br/>
                # Luego ejecuta:<br/>
                npm start
              </code>
            </div>

            <div>
              <h3 className="font-medium mb-2">2. Verificar configuración en Google Cloud Console</h3>
              <p className="text-blue-700 mb-2">
                Accede a: <a href="https://console.cloud.google.com/google/maps-apis" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console - Maps APIs</a>
              </p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Verifica que las siguientes APIs estén habilitadas:
                  <ul className="list-circle list-inside ml-4">
                    <li>Maps JavaScript API</li>
                    <li>Places API</li>
                  </ul>
                </li>
                <li>Ve a "Credenciales" y verifica tu API key</li>
                <li>En "Restricciones de aplicación", asegúrate de que:
                  <ul className="list-circle list-inside ml-4">
                    <li>Si usas "Restricciones de referente HTTP", incluye: <code className="bg-blue-100 px-1">http://localhost:3000/*</code></li>
                    <li>O mejor aún, selecciona "Ninguna" mientras desarrollas</li>
                  </ul>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium mb-2">3. Revisar la consola del navegador</h3>
              <p className="text-blue-700">
                Presiona F12 para abrir las herramientas de desarrollador y busca errores en la pestaña "Console".
              </p>
            </div>

            <div>
              <h3 className="font-medium mb-2">4. Errores comunes</h3>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li><strong>RefererNotAllowedMapError:</strong> Tu API key tiene restricciones de referente. Agrega localhost a la lista de referentes permitidos.</li>
                <li><strong>ApiNotActivatedMapError:</strong> No has habilitado Places API en Google Cloud Console.</li>
                <li><strong>InvalidKeyMapError:</strong> La API key es inválida o fue revocada.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Console Logs */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Debug Information</h2>
          <div className="text-xs font-mono space-y-1">
            <div>window.google exists: {String(!!window.google)}</div>
            <div>window.google.maps exists: {String(!!(window.google && window.google.maps))}</div>
            <div>window.google.maps.places exists: {String(!!(window.google && window.google.maps && window.google.maps.places))}</div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default GoogleMapsTest;
